/**
 * T-051: editable Pocket check-in photo upload limits.
 *
 * Lives in the Pocket > Setup tab. Two values: max bytes per photo
 * and max photo count per check-in. Stored in pocket_settings; the
 * multer factory in admin/checkins reads them at request time so
 * edits take effect within 60s.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'react-toastify';
import { useRBAC } from '@/contexts/RBACContext';
import {
  getCheckinPhotoConfig,
  updateCheckinPhotoConfig,
} from '@/lib/pocketSettingsApi';
import type { PocketCheckinPhotoConfig } from '@/types/pocketSettings';

function bytesToMb(bytes: number): number {
  return Math.round((bytes / (1024 * 1024)) * 10) / 10;
}

function mbToBytes(mb: number): number {
  return Math.round(mb * 1024 * 1024);
}

export function PhotoCheckinConfigCard() {
  const { isSuperadmin } = useRBAC();
  const [config, setConfig] = useState<PocketCheckinPhotoConfig | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftMb, setDraftMb] = useState('');
  const [draftCount, setDraftCount] = useState('');
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    getCheckinPhotoConfig()
      .then((data) => {
        if (!cancelled) setConfig(data);
      })
      .catch(() => {
        // Non-fatal.
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  function startEdit() {
    if (!config) return;
    setDraftMb(String(bytesToMb(config.max_bytes)));
    setDraftCount(String(config.max_count));
    setEditing(true);
  }

  async function saveEdit() {
    if (!config) return;
    const mb = Number(draftMb);
    const count = Number(draftCount);
    const minMb = bytesToMb(config.bounds.max_bytes.min);
    const maxMb = bytesToMb(config.bounds.max_bytes.max);
    if (
      !Number.isFinite(mb) ||
      mb < minMb ||
      mb > maxMb ||
      !Number.isInteger(count) ||
      count < config.bounds.max_count.min ||
      count > config.bounds.max_count.max
    ) {
      toast.error(
        `Photo size must be ${minMb}-${maxMb} MB. Photo count must be ${config.bounds.max_count.min}-${config.bounds.max_count.max}.`,
      );
      return;
    }
    setSaving(true);
    try {
      await updateCheckinPhotoConfig({
        max_bytes: mbToBytes(mb),
        max_count: count,
      });
      toast.success('Photo limits saved.');
      setEditing(false);
      refresh();
    } catch {
      toast.error('Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">
          Check-in photo limits
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Per-photo size cap and max number of additional photos a driver can
          attach to a check-in. Tighter limits = smaller storage bills + faster
          uploads on weak cell. Looser = more freedom for damage documentation.
        </p>

        {!config ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="rounded-md border border-border/50 p-4 bg-card">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-muted-foreground">Max size</div>
                  {!editing && !config.is_default ? (
                    <Badge variant="secondary" className="text-xs">
                      override
                    </Badge>
                  ) : null}
                </div>
                {editing ? (
                  <Input
                    type="number"
                    step="0.5"
                    min={bytesToMb(config.bounds.max_bytes.min)}
                    max={bytesToMb(config.bounds.max_bytes.max)}
                    value={draftMb}
                    onChange={(e) => setDraftMb(e.target.value)}
                    className="mt-1"
                    aria-label="max_mb"
                  />
                ) : (
                  <div className="text-2xl font-semibold tabular-nums mt-1">
                    {bytesToMb(config.max_bytes)} MB
                  </div>
                )}
              </div>
              <div className="rounded-md border border-border/50 p-4 bg-card">
                <div className="text-xs text-muted-foreground">Max photos</div>
                {editing ? (
                  <Input
                    type="number"
                    min={config.bounds.max_count.min}
                    max={config.bounds.max_count.max}
                    value={draftCount}
                    onChange={(e) => setDraftCount(e.target.value)}
                    className="mt-1"
                    aria-label="max_count"
                  />
                ) : (
                  <div className="text-2xl font-semibold tabular-nums mt-1">
                    {config.max_count}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {editing ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(false)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" onClick={saveEdit} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </>
              ) : isSuperadmin ? (
                <Button size="sm" variant="outline" onClick={startEdit}>
                  Edit
                </Button>
              ) : null}
            </div>

            <p className="mt-3 text-xs text-muted-foreground">
              <strong>After saving:</strong> the next check-in upload uses the
              new limits (server-side multer config picks up the change within
              60s). In-flight uploads are unaffected.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
